import { sum } from 'lodash';
import type { GetSleepOnDateRes, UnifiedChatRichContext } from '../../../../data-access';
import { MealType, MetricMealDetail } from '@demind-inc/core';
import { AI_SCHEDULER_EVENT_PREFIX, translateSecondsToHourAndMinutes } from '../../../../helpers';
import {
  isSleepSummaryLike,
  normalizeRichContentType,
} from '../../../../components/AiAssistantChat/RichContext/contentCardUtils';

/** Strip HTML from AI/chat-backed descriptions so calendar notes stay plain text. */
export function plainTextFromHtmlDescription(html: string): string {
  if (!html || typeof html !== 'string') {
    return '';
  }
  const withoutFooter = html.replace(
    /\[Created from Lifestack \(https:\/\/lifestack\.ai\)\]./g,
    ''
  );
  const withBreaks = withoutFooter
    .replace(/<\/p>\s*/gi, '\n')
    .replace(/<p[^>]*>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/div>\s*/gi, '\n')
    .replace(/<[^>]+>/g, '');
  const decoded = withBreaks
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");
  return decoded.replace(/\n{3,}/g, '\n\n').trim();
}

/** Preview / chat CTA / AI scheduler blocks — not a persisted calendar row yet. */
export function isTemporalAISuggestedEventId(eventId?: string | null): boolean {
  if (!eventId) {
    return true;
  }
  const id = String(eventId);
  return id.startsWith('temporal') || id.startsWith(AI_SCHEDULER_EVENT_PREFIX);
}

export const buildContextMessageFromData = (startTime: string, endTime: string, name: string) => {
  const realData = `[Actual data of ${name}] Start time: ${startTime} End time: ${endTime}.`;

  return realData;
};

export const hasSleepSummaryDataInRichContext = (
  richContext?: UnifiedChatRichContext | null
): boolean => {
  if (!richContext) return false;

  const candidatePayloads: unknown[] = [];
  const addCandidate = (candidate: unknown) => {
    if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
      candidatePayloads.push(candidate);
    }
  };

  const rawContent = richContext.content;
  const contentList = rawContent ? (Array.isArray(rawContent) ? rawContent : [rawContent]) : [];

  for (const content of contentList) {
    if (!content || typeof content !== 'object') continue;

    const type = typeof (content as any).type === 'string' ? (content as any).type.trim() : '';
    const payload =
      (content as any).payload &&
      typeof (content as any).payload === 'object' &&
      !Array.isArray((content as any).payload)
        ? ((content as any).payload as Record<string, unknown>)
        : null;

    if (!payload) continue;

    if (normalizeRichContentType(type) === 'sleep_summary') {
      addCandidate(payload);
    }
    addCandidate(payload.sleep_summary);
    addCandidate(payload.sleepsummary);
    addCandidate(payload.sleep);
  }

  if (Array.isArray(richContext.signals)) {
    for (const signal of richContext.signals) {
      const data =
        signal?.data && typeof signal.data === 'object' && !Array.isArray(signal.data)
          ? (signal.data as Record<string, unknown>)
          : null;
      if (!data) continue;

      addCandidate(data);
      addCandidate(data.sleep_summary);
      addCandidate(data.sleepsummary);
      addCandidate(data.sleep);
    }
  }

  return candidatePayloads.some((candidate) => isSleepSummaryLike(candidate));
};

export const buildContextMessageFromSleep = (sleepData: GetSleepOnDateRes) => {
  const parts: string[] = [];

  // Always include start and end time if they exist
  if (sleepData.sleepTime && sleepData.awakeTime) {
    parts.push(`Start time: ${sleepData.sleepTime} End time: ${sleepData.awakeTime}`);
  }

  // Include total sleep hours if both hours and minutes are defined
  if (
    sleepData.totalSleepHours?.hours !== undefined &&
    sleepData.totalSleepHours?.minutes !== undefined
  ) {
    parts.push(
      `Total sleep: ${sleepData.totalSleepHours.hours} hours ${sleepData.totalSleepHours.minutes} minutes`
    );
  }

  // Include sleep stage if defined

  if (sleepData.sleepStage && sum(Object.values(sleepData.sleepStage ?? {})) > 0) {
    parts.push(
      `Sleep stage: ${Object.entries(sleepData.sleepStage)
        .map(([key, value]) => {
          const { hours, minutes } = translateSecondsToHourAndMinutes(value);
          return `${key}: ${hours} hours ${minutes} minutes`;
        })
        .join(', ')}`
    );
  }

  // Include data source if defined
  if (sleepData.sleepSource) {
    parts.push(`Data source: ${sleepData.sleepSource}`);
  }

  // Include average wake time if defined
  if (sleepData.avgWakeTimeHHmm) {
    parts.push(`Average wake time: ${sleepData.avgWakeTimeHHmm}`);
  }

  // Include average sleep time if defined
  if (sleepData.avgSleepTimeHHmm) {
    parts.push(`Average sleep time: ${sleepData.avgSleepTimeHHmm}`);
  }

  const realData = `[Actual data of sleep] ${parts.join('. ')}.`;

  return realData;
};

export const buildContextMessageFromMeal = (mealData: MetricMealDetail) => {
  const parts: string[] = [];

  if (mealData.type) {
    parts.push(`Meal type: ${mealData.type}`);
  }

  if (mealData.startTime) {
    parts.push(`Start time: ${mealData.startTime}`);
  }

  if (mealData.endTime) {
    parts.push(`End time: ${mealData.endTime}`);
  }

  if (mealData.source) {
    parts.push(`Source: ${mealData.source}`);
  }
  if (mealData.micros) {
    parts.push(
      `Micros: ${Object.entries(mealData.micros)
        .filter(([_, value]) => value > 0)
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ')}`
    );
  }
  if (mealData.macros) {
    parts.push(
      `Macros: ${Object.entries(mealData.macros)
        .filter(([_, value]) => value > 0)
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ')}`
    );
  }

  const realData = `[Actual data of meal] ${parts.join('. ')}.`;

  return realData;
};

/**
 * Returns permissions and mealType for an AI suggested event.
 * @param aiSuggestedEvent The event object (may be undefined)
 * @param sessionType The session type string (may be undefined)
 * @param name The event name/summary (may be undefined)
 * @param getMealTypeFromName Function to get meal type from name
 */
export function getAISuggestedEventPermissions({
  eventId,
  sessionType,
  mealType,
}: {
  eventId?: any;
  sessionType?: string;
  mealType?: MealType;
}) {
  let canUpdate = false; // Update the existing event
  let canAcceptCalendar = false; // Create a new event by accepting
  let canReject = false; // Reject the suggestion
  let canDelete = false; // Delete the event

  if (sessionType === 'sleep') {
    canUpdate = true;
    canDelete = true;
  } else if (sessionType === 'meal') {
    if (mealType) {
      canUpdate = true;
    }
    canDelete = true;
  } else if (sessionType === 'task') {
    if (isTemporalAISuggestedEventId(eventId)) {
      canAcceptCalendar = true;
      canReject = true;
    } else {
      canUpdate = true;
      canDelete = true;
    }
  } else if (sessionType === 'routine') {
    canUpdate = true;
    canDelete = true;
  } else if (sessionType === 'workout') {
    if (isTemporalAISuggestedEventId(eventId)) {
      canAcceptCalendar = true;
      canReject = true;
    } else {
      canUpdate = true;
      canDelete = true;
    }
  }

  return {
    canUpdate,
    canAcceptCalendar,
    canReject,
    canDelete,
  };
}
